import React, { FC, useEffect, useState } from 'react';

import { LoadingOutlined } from '@ant-design/icons';
import { Checkbox } from 'antd';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import {
  getEmailTest,
  getMCInfo,
  postDataCall,
  putDataCall,
} from 'services/api';
import apiPaths from 'services/apiPaths';
import { updateMCSubscription } from 'src/actions/authActions';
import {
  newsletterModalContent,
  renderEmailSubscription,
} from 'src/components/account/mailchimpModals';
import CustomModal from 'src/components/CustomModal';
import { MCUserStatus } from 'src/shared/enums';
import { IUser } from 'src/shared/models';
import { getMCStatus } from 'utils';
import { openToast } from 'src/components/general/Toast';
import { useRouter } from 'next/router';

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators(
    {
      updateMCSubscription,
    },
    dispatch,
  );

interface OwnProps {
  user: IUser;
}

type Props = OwnProps & ReturnType<typeof mapDispatchToProps>;

const MCCheck: FC<Props> = ({ user, updateMCSubscription }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [newsletterActive, setNewsletterActive] = useState<boolean>(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalEmailSubscriptionVisible, setModalEmailSubscriptionVisible] =
    useState<boolean>(false);
  const [newsletterSegmented, setNewsletterSegmented] =
    useState<boolean>(false);
  const { formatMessage } = useIntl();
  const getCurrentUrl = () => {
    return window.location.href;
  };

  const router = useRouter();
  const { query } = router;

  useEffect(() => {
    const newsletterStatus = getMCStatus(user?.mcSubscriptionStatus);
    setNewsletterActive(newsletterStatus);

    const newsletterSegmentedStatus = getMCStatus(
      user?.mcSegmentedSubscription,
    );
    setNewsletterSegmented(newsletterSegmentedStatus);
  }, [user]);

  useEffect(() => {
    const subscribeMC = !!query.subscription && query.subscription === 'true';
    const isUnSub = user.mcSubscriptionStatus === MCUserStatus.UNSUBSCRIBED;
    const isUnSub2 = user.mcSegmentedSubscription === MCUserStatus.UNSUBSCRIBED;
    if (subscribeMC && (isUnSub || isUnSub2)) {
      handleSubscription();
    }
  }, []);

  const handleSubscription = async () => {
    const { UNSUBSCRIBE, SUBSCRIBE } = apiPaths.MC_NEWSLETTER;
    const dataPath = newsletterActive ? UNSUBSCRIBE : SUBSCRIBE;

    try {
      setLoading(true);
      await postDataCall({
        dataPath,
        data: {
          isDermo: user.dermo?.isDermo,
          currentUrl: getCurrentUrl(),
        },
        callConfig: {},
      });

      updateMCSubscription(
        newsletterActive ? MCUserStatus.UNSUBSCRIBED : MCUserStatus.SUBSCRIBED,
        newsletterActive ? MCUserStatus.UNSUBSCRIBED : MCUserStatus.SUBSCRIBED,
      );

      openToast(
        formatMessage({
          id: `page.account.newsletter.${
            newsletterActive ? 'un' : ''
          }subscribe`,
        }),
        'success',
        5,
      );

      await postDataCall({
        dataPath: apiPaths.AUTH.VALIDATE_PROFILING,
        data: { email: user.email },
        callConfig: {},
      });

      setNewsletterActive(!newsletterActive);
      setNewsletterSegmented(!newsletterActive);
    } catch (error) {
      if (error.response) {
        const { data } = error.response;
        openToast(
          formatMessage(
            { id: `page.account-newsletter.response-error` },
            { code: data.code, error: data.error },
          ),
          'error',
          5,
        );
      }
    } finally {
      setModalVisible(false);
      setLoading(false);
    }
  };

  const handleNewsletterSubscription = async () => {
    switch (user.mcSubscriptionStatus) {
      case MCUserStatus.SUBSCRIBED:
        setModalVisible(true);
        break;
      case MCUserStatus.UNSUBSCRIBED:
        await handleSubscription();
        break;
      default:
        openToast(
          formatMessage({
            id: `page.account-newsletter.mc.subscription-error`,
          }),
          'error',
          5,
        );
    }
  };

  const subscriptionModals = (
    <>
      <CustomModal
        {...{
          content: newsletterModalContent(
            loading,
            handleSubscription,
            formatMessage,
          ),
          customClass: 'con__subscribe_modal',
          hideFooter: true,
          visible: modalVisible,
          onCancel: () => setModalVisible(false),
          showIcon: false,
        }}
      />
      <CustomModal
        {...{
          content: renderEmailSubscription(formatMessage),
          hideFooter: true,
          visible: modalEmailSubscriptionVisible,
          onCancel: () => setModalEmailSubscriptionVisible(false),
          showIcon: false,
        }}
      />
    </>
  );

  return (
    <>
      {loading ? (
        <LoadingOutlined className="newsletter-switch" />
      ) : (
        <Checkbox
          onChange={handleNewsletterSubscription}
          checked={newsletterActive}>
          {newsletterActive && !newsletterSegmented
            ? formatMessage({ id: 'form.checkbox.subscribe-newsletter' })
            : formatMessage({
                id: 'form.checkbox.subscribe-newsletter-comercial',
              })}
        </Checkbox>
      )}
      {subscriptionModals}
    </>
  );
};

export default connect(null, mapDispatchToProps)(MCCheck);
